import type { ReactNode } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  /** Footer actions, typically Cancel + a primary button. */
  footer?: ReactNode;
  children?: ReactNode;
  /** Tailwind max-width for the centered card. Defaults to max-w-md. */
  size?: string;
  /** 'center' (modal card) or 'drawer' (full-height right panel). */
  variant?: 'center' | 'drawer';
  /** Override the body wrapper classes (e.g. '' to manage padding per-item). */
  bodyClassName?: string;
}

/**
 * The one Dialog primitive for the whole app. Wraps @radix-ui/react-dialog,
 * which provides — for free, on every consumer — a real focus trap, Escape to
 * close, focus restoration to the trigger, scroll-lock, and ARIA wiring. The
 * bespoke modals/drawer (PingDialog, DeploymentDialog, NotResolvedDialog,
 * ActivityDrawer) had none of these; this replaces that pattern.
 *
 * The body scrolls (max-height capped) so tall content stays usable while the
 * header/footer remain pinned. `variant='drawer'` renders a right-side panel
 * (Activity) using the shared slide-in-right entrance.
 *
 * Styling mirrors the previous card/drawer patterns so the visual change is
 * minimal while the behavior becomes correct.
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  footer,
  children,
  size = 'max-w-md',
  variant = 'center',
  bodyClassName = 'p-4 space-y-3',
}: DialogProps) {
  const isDrawer = variant === 'drawer';
  const contentCls = isDrawer
    ? 'fixed top-0 right-0 z-50 h-full w-80 sm:w-96 bg-white border-l border-slate-200 shadow-xl flex flex-col focus:outline-none animate-slide-in-right'
    : `fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] ${size} max-h-[85vh] bg-white rounded-lg shadow-xl flex flex-col focus:outline-none`;

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className={`fixed inset-0 z-40 ${isDrawer ? 'bg-slate-900/20' : 'bg-slate-900/40'}`} />
        <RadixDialog.Content className={contentCls}>
          <div className="shrink-0 p-4 border-b border-slate-200 flex items-start justify-between gap-4">
            <div>
              <RadixDialog.Title className="text-base font-semibold text-slate-900">
                {title}
              </RadixDialog.Title>
              {description && (
                <RadixDialog.Description className="text-xs text-slate-600 mt-1">
                  {description}
                </RadixDialog.Description>
              )}
            </div>
            <RadixDialog.Close
              className="p-1 -m-1 text-slate-500 hover:text-slate-900 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              aria-label="Close"
            >
              <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M4.3 4.3a1 1 0 011.4 0L10 8.6l4.3-4.3a1 1 0 111.4 1.4L11.4 10l4.3 4.3a1 1 0 11-1.4 1.4L10 11.4l-4.3 4.3a1 1 0 11-1.4-1.4L8.6 10 4.3 5.7a1 1 0 010-1.4z" />
              </svg>
            </RadixDialog.Close>
          </div>
          {children && <div className={`flex-1 overflow-y-auto ${bodyClassName}`}>{children}</div>}
          {footer && (
            <div className="shrink-0 p-4 border-t border-slate-200 flex justify-end gap-2">{footer}</div>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
